import dayjs from 'dayjs';

import type { ExperienceDurationDescription } from 'app/typings/core/common/experiences/enum/experienceDurationDescription';
import type { ExtranetHotelExperience } from 'app/typings/core/extranet/hotel/experiences';
import { pluralize } from 'app/utils/strings';

import type { ExperienceOpening } from '../../utils/experiences';
import type { PackDetails } from '../../utils/packs';
import type { RoomOpeningInventory } from '../../utils/rooms';

const formatDuration = (
  duration?: number,
  durationDescription?: ExperienceDurationDescription
): string | null => {
  if (!duration || !durationDescription) {
    return null;
  }

  const durationMap: Record<ExperienceDurationDescription, string> = {
    MINUTE: pluralize(duration, 'min', 'mins'),
    HOUR: pluralize(duration, 'hour', 'hours'),
    HALF_DAY: 'half day',
    DAY: pluralize(duration, 'day', 'days'),
  };

  return durationMap[durationDescription];
};

export const formatExperienceName = (
  experience: ExtranetHotelExperience,
  categoryName?: string
): string => {
  const parts: string[] = [];

  if (categoryName) {
    parts.push(categoryName);
  }

  if (experience.brand) {
    parts.push(experience.brand);
  }

  const duration = formatDuration(
    experience.duration,
    experience.durationDescription
  );

  if (duration) {
    parts.push(duration);
  }

  if (experience.time) {
    parts.push(dayjs(`1970-01-01T${experience.time}`).format('HH:mm'));
  }

  if (experience.location) {
    parts.push(experience.location);
  }

  return parts.join(' • ');
};

export type EnabledMessage =
  | ExperienceOpening['message']
  | RoomOpeningInventory['message']
  | PackDetails['message'];

const messageEnabledMap: Record<EnabledMessage, boolean> = {
  PUBLISHED: true,
  MISSING_STOCK: true,
  FUTURE_SALES: true,
  MANUALLY_CLOSED: true,
  MISSING_PRICE: true,
  MISSING_DISCOUNT_PRICE: true,
  DISCOUNT_PRICE_TOO_LOW: true,
  CLOSED_BY_STAYCATION: true,
  CLOSED_ON_CM: true,
  MISSING_EXPERIENCES_STOCK: true,
  MISSING_ROOM_STOCK: true,
  MAX_BOOKING_REACHED: true,
  STOP_SELL: true,
  CLOSING_PERIOD: false,
  CLOSED_BEFORE_CHECKIN: false,
  PAST_SALES: false,
  STOCK_CLOSED: false,
};

export const isEnabledByMessage = (message?: EnabledMessage): boolean =>
  message === null || message === undefined
    ? true
    : messageEnabledMap[message] ?? true;
